import { Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useCurrency } from "../../context/CurrencyContext";
import { usePortfolio } from "../../context/PortfolioContext";
import { ValueStatistics } from "../../shared/ValueStatistics";
import { extremeFigure, Figure, FigureCard } from "./ClientsPortfolioParts";
import { cardRow, SectionHeading } from "./PortfolioSection";

/**
 * What the clients claim and what is claimed against them, as two cards that
 * mirror each other: a high claim is good for the client that makes it and
 * bad for the one it is made against.
 */
const QuantumClientsPortfolio = () => {
  const { currency } = useCurrency();
  const { t } = useTranslation(undefined, { keyPrefix: "portfolio.quantum" });
  const {
    quantumsClaimed,
    numberOfCases,
    totalQuantumsClaimed,
    quantumsClaimedAgainst,
    totalQuantumsClaimedAgainst,
  } = usePortfolio();

  const claims = (
    side: "byClients" | "againstClients",
    claimed: ValueStatistics,
    // how many amounts are claimed
    amounts: number,
  ) => {
    const end = (key: "max" | "min", tone: NonNullable<Figure["tone"]>) =>
      extremeFigure(claimed[key], {
        label: t(`cards.${key}.title`),
        tone,
        format: currency.format,
        who: ({ partyName, caseName }) =>
          t(`${side}.who`, { partyName, caseName }),
        page: "configuration/claims",
      });
    const highIsGood = side === "byClients";

    return (
      <FigureCard
        title={t(`${side}.title`)}
        figures={[
          {
            label: t("cards.average.title"),
            value: currency.format(claimed.avg),
            note: t("cards.average.subTitle", { count: numberOfCases }),
          },
          {
            label: t("cards.averageAmount.title"),
            value: currency.format(amounts === 0 ? 0 : claimed.total / amounts),
            note: t("cards.averageAmount.subTitle", { count: amounts }),
          },
          end("max", highIsGood ? "favourable" : "adverse"),
          end("min", highIsGood ? "adverse" : "favourable"),
        ]}
      />
    );
  };

  return (
    <>
      <SectionHeading id="quantums" rule>
        {t("title")}
      </SectionHeading>
      <Box sx={cardRow}>
        {claims("byClients", quantumsClaimed, totalQuantumsClaimed)}
        {claims(
          "againstClients",
          quantumsClaimedAgainst,
          totalQuantumsClaimedAgainst,
        )}
      </Box>
    </>
  );
};

export default QuantumClientsPortfolio;
